import type { RequestPayload } from "@inertiajs/core";
import { usePage } from "@inertiajs/react";
import { Checkbox, Divider, Fieldset, Stack, Tabs } from "@mantine/core";
import { isNotEmpty, useForm } from "@mantine/form";
import { useState } from "react";
import { renderEmailPreview } from "../../helpers/emailPreviewHelper";
import { notEmptyMessage } from "../../helpers/formHelper";
import type { ApprenticeSupervisor } from "../../models/apprenticeSupervisor";
import type { Company } from "../../models/company";
import { SaveButton } from "../button/SaveButton";
import { EmailEditorForm, type EmailFormType, emailTemplateValidation, PlaceholderGroup } from "../email/EmailEditorForm";
import { ParticularsForm, type ParticularsFormErrorType, type ParticularsFormType } from "../particularsForm/ParticularsForm";
import { FilterSelect } from "../select/FilterSelect";
import { UserForm, type UserFormErrorTypes, type UserFormType } from "../user/UserForm";

type ApprenticeSupervisorFormProps = {
  apprenticeSupervisor?: Partial<ApprenticeSupervisor>;
  submitButtonLabel: string;
  onSubmit: (values: RequestPayload) => void;
  companies: Company[];
};

type ApprenticeSupervisorFormType = UserFormType &
  EmailFormType &
  ParticularsFormType & {
    company_id: string | undefined;
    send_mail: boolean;
  };

type FormErrors = UserFormErrorTypes &
  ParticularsFormErrorType & {
    company?: string[];
  };

export function ApprenticeSupervisorForm({ apprenticeSupervisor, companies, onSubmit, submitButtonLabel }: ApprenticeSupervisorFormProps) {
  const { errors } = usePage<{ errors: FormErrors }>().props;

  const form = useForm<ApprenticeSupervisorFormType>({
    mode: "uncontrolled",
    initialValues: {
      salutation: apprenticeSupervisor?.salutation,
      first_name: apprenticeSupervisor?.first_name,
      last_name: apprenticeSupervisor?.last_name,
      company_id: apprenticeSupervisor?.company?.id.toString(),
      user_attributes: {
        email: apprenticeSupervisor?.user?.email,
        password: "",
        active: apprenticeSupervisor?.user?.active ?? true,
      },
      email_template: apprenticeSupervisor?.email_template,
      subject: apprenticeSupervisor?.subject,
      send_mail: apprenticeSupervisor?.send_mail ?? true,
    },
    validate: {
      subject: (value, values) => (values.send_mail ? isNotEmpty(notEmptyMessage)(value) : null),
      email_template: (value, values) => (values.send_mail ? emailTemplateValidation(value) : null),
    },
  });
  const companyOptions = companies.map(({ name, id }) => ({
    label: name,
    value: id.toString(),
  }));
  const [previewText, setPreviewText] = useState("");
  const onPreview = () => setPreviewText(renderEmailPreview(form.getValues().email_template));

  return (
    <form onSubmit={form.onSubmit((values) => onSubmit(values))}>
      <Tabs defaultValue="data">
        <Tabs.List mb="md">
          <Tabs.Tab
            value="data"
            styles={{
              tabLabel: {
                color: Object.keys(form.errors).some((key) => key !== "email_template" && key !== "subject") ? "red" : undefined,
              },
            }}
          >
            Daten
          </Tabs.Tab>
          <Tabs.Tab
            value="email"
            styles={{
              tabLabel: { color: form.errors?.subject || form.errors?.email_template ? "red" : undefined },
            }}
          >
            E-Mail
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="data">
          <Fieldset legend="Details">
            <Stack w="60rem">
              <ParticularsForm form={form} errors={errors} />
              <UserForm form={form} errors={errors} />
              <Stack>
                <FilterSelect
                  withAsterisk
                  data={companyOptions}
                  key={form.key("company_id")}
                  clearable
                  label="Lehrbetrieb"
                  {...form.getInputProps("company_id")}
                  error={errors?.company}
                />
              </Stack>
              <Divider />
              <SaveButton label={submitButtonLabel} />
            </Stack>
          </Fieldset>
        </Tabs.Panel>
        <Tabs.Panel value="email">
          <EmailEditorForm
            form={form}
            previewText={previewText}
            onPreview={onPreview}
            saveButtonLabel={submitButtonLabel}
            required={form.getValues().send_mail}
            showPlaceholderGroups={PlaceholderGroup.Receiver}
          >
            <Checkbox label="E-Mail senden" key={form.key("send_mail")} {...form.getInputProps("send_mail", { type: "checkbox" })} />
          </EmailEditorForm>
        </Tabs.Panel>
      </Tabs>
    </form>
  );
}
